import { useQuery } from '@tanstack/react-query';
import { getProjectEvaluations } from '../../api/project-evaluations';
import { useProductSettings } from '@workspace/utils-react';
import type { Settings } from '../../AppContainer';
import type { ProjectEvaluation } from '../../api/shared/types';

export const projectEvaluationsQueryKey = (fiscalYearQuarter: string) =>
  ['projectEvaluations', fiscalYearQuarter] as const;

export function useProjectEvaluations(fiscalYearQuarter: string) {
  const { apiUrl } = useProductSettings<Settings>();
  return useQuery({
    queryKey: projectEvaluationsQueryKey(fiscalYearQuarter),
    queryFn: () => getProjectEvaluations(apiUrl, fiscalYearQuarter),
    enabled: !!fiscalYearQuarter,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook that fetches project evaluations for multiple fiscal year quarters
 * and builds a lookup map: projectName -> { [fiscalYearQuarter]: ProjectEvaluation }
 */
export function useMultipleQuartersProjectEvaluations(fiscalYearQuarters: string[]) {
  const { apiUrl } = useProductSettings<Settings>();

  return useQuery({
    queryKey: ['projectEvaluations', 'multiple', ...fiscalYearQuarters],
    queryFn: async () => {
      const results = await Promise.allSettled(
        fiscalYearQuarters.map((fyq) => getProjectEvaluations(apiUrl, fyq))
      );

      const lookup: Record<string, Record<string, ProjectEvaluation>> = {};

      for (const result of results) {
        if (result.status === 'fulfilled') {
          for (const evaluation of result.value) {
            if (!lookup[evaluation.project_name]) {
              lookup[evaluation.project_name] = {};
            }
            lookup[evaluation.project_name][evaluation.fiscal_year_quarter] = evaluation;
          }
        }
      }

      return lookup;
    },
    enabled: fiscalYearQuarters.length > 0,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
}
